import { Button, Radio, Typography } from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { updateBranch } from '@/api/user';

import { Icon } from '@/components/Icon/Icon.tsx';
import { Divider } from '@/components/Layout/Divider.tsx';
import { Drawer, DrawerProps } from '@/components/Layout/Drawer';

import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { notifyUserChanged } from '@/redux/auth/actions';
import { selectTerritories } from '@/redux/countries/selectors';

import { User } from '@/types/user';

import {
  BranchContainer,
  BranchContainerItem,
  BranchInformation,
  BranchListCard,
  BranchPhoneText,
  BranchSelectionDrawerContainer,
} from './BranchSelectionDrawer.styled';

type BranchProps = {
  showDivider: boolean;
  checked: boolean;
  branchName: string;
  address: string;
  telephone: string;
  onClick: () => void;
};

function Branch({
  checked,
  branchName,
  address,
  telephone,
  onClick,
  showDivider,
}: Readonly<BranchProps>) {
  return (
    <BranchContainer onClick={onClick}>
      <BranchContainerItem>
        <BranchInformation>
          <strong>{branchName}</strong>
          <div>{address}</div>
          <BranchPhoneText>
            <Icon name="phone-outline" size={20} />
            {telephone}
          </BranchPhoneText>
        </BranchInformation>
        <Radio checked={checked} />
      </BranchContainerItem>
      {showDivider && (
        <Divider
          width="96%"
          containerProps={{
            mx: 4,
          }}
        />
      )}
    </BranchContainer>
  );
}

interface BranchSelectionDrawerProps extends DrawerProps {
  user: User;
}
export function BranchSelectionDrawer({
  user,
  ...props
}: Readonly<BranchSelectionDrawerProps>) {
  const { t } = useTranslate();
  const dispatch = useAppDispatch();
  const { territoryByCountryCode } = useAppSelector(selectTerritories);
  const territoryCode = user?.territoryCode || '';
  const theme = useTheme();
  const branches = useMemo(() => {
    return territoryCode ? territoryByCountryCode[territoryCode].branches : [];
  }, [territoryByCountryCode, territoryCode]);

  const { control, watch, reset } = useForm({
    defaultValues: { branch: user?.visitingBranchId || '' },
  });

  useEffect(() => {
    reset({ branch: user?.visitingBranchId || '' });
  }, [user?.visitingBranchId, reset]);

  const selectedBranch = watch('branch');

  const { mutate, isPending } = useMutation({
    mutationFn: updateBranch,
    onSuccess: () => {
      props.onClose();
      dispatch(notifyUserChanged());
    },
  });

  const onSelectBranch = (branchId: string) => () => {
    mutate(branchId);
  };

  return (
    <Drawer
      {...props}
      headerBgColor={(theme: Theme) => theme.palette.neutral[100]}
      bgColor={(theme: Theme) => theme.palette.neutral[100]}
      title={t('moreInfo.form.branchSelection.title')}
    >
      <BranchSelectionDrawerContainer>
        <Typography variant="h3" textAlign="center" mt={1} mb={2}>
          {t('branchSelectionDrawer.title')}
        </Typography>
        <Typography
          variant="body2"
          textAlign="center"
          color={theme.palette.neutral[800]}
          mb={4}
        >
          {t('branchSelectionDrawer.description')}
        </Typography>

        <BranchListCard>
          <Typography variant="h4" textAlign="center" mb={2}>
            {t('branchSelectionDrawer.form.title')}
          </Typography>
          <Controller
            control={control}
            name="branch"
            render={({ field }) => (
              <>
                {branches.map((branch, idx) => (
                  <Branch
                    key={branch.id}
                    branchName={branch.branchName}
                    address={branch.address}
                    telephone={branch.telephone}
                    checked={branch.id === field.value}
                    onClick={() => field.onChange(branch.id)}
                    showDivider={idx !== branches.length - 1}
                  />
                ))}
              </>
            )}
          />
        </BranchListCard>

        <Button
          onClick={onSelectBranch(selectedBranch)}
          disabled={!selectedBranch || isPending}
        >
          {t('branchSelectionDrawer.form.button.save')}
        </Button>
      </BranchSelectionDrawerContainer>
    </Drawer>
  );
}
